import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { 
  Mail, Send, CheckCircle, XCircle, AlertTriangle, Clock, 
  MapPin, User, Calendar, FileText, Eye, Camera, Image,
  ArrowLeft, Paperclip, Download, Upload, Trash2, ExternalLink, 
  Home, Phone, Navigation, Building2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ANNEXURE_TYPES = [
  { id: 'house_photo_1', label: 'House Photo 1' },
  { id: 'house_photo_2', label: 'House Photo 2' },
  { id: 'landmark_photo_1', label: 'Landmark Photo 1' },
  { id: 'landmark_photo_2', label: 'Landmark Photo 2' },
  { id: 'verifier_id_proof', label: 'Verifier ID Proof' },
  { id: 'fe_selfie', label: 'Field Executive Selfie' },
  { id: 'address_proof', label: 'Address Proof Document' },
  { id: 'other', label: 'Other' }
];

const VERIFICATION_FIELDS = [
  { id: 'candidate_name', label: 'Name of Candidate', required: true },
  { id: 'father_name', label: "Father's Name", required: true },
  { id: 'contact_number', label: 'Contact Number', required: true },
  { id: 'alternate_number', label: 'Alternate Number', required: false },
  { id: 'address', label: 'Full Address', required: true },
  { id: 'landmark', label: 'Landmark', required: false },
  { id: 'city', label: 'City', required: true },
  { id: 'state', label: 'State', required: true },
  { id: 'pincode', label: 'Pincode', required: true },
  { id: 'period_of_stay', label: 'Period of Stay', required: true },
  { id: 'residence_status', label: 'Residence Status (Own/Rented)', required: true },
  { id: 'nearest_police_station', label: 'Nearest Police Station', required: false },
  { id: 'marital_status', label: 'Marital Status', required: false },
  { id: 'family_members', label: 'Members in Family', required: false },
  { id: 'person_contacted_name', label: 'Person Contacted - Name', required: true },
  { id: 'person_contacted_relationship', label: 'Relationship with Candidate', required: true },
  { id: 'person_contacted_number', label: 'Contact Number of Person', required: false },
  { id: 'neighbour_1', label: 'Neighbour 1 Details', required: false },
  { id: 'neighbour_2', label: 'Neighbour 2 Details', required: false }
];

const NOT_VERIFIED_REASONS = [
  'Address Not Found',
  'Candidate Not Known at Address',
  'Door Locked / No One Available',
  'Shifted to New Address',
  'Wrong/Incomplete Address',
  'Building/House Demolished',
  'Access Denied by Resident',
  'Unsafe Area / Unable to Visit',
  'Other'
];

const DISCREPANCY_REASONS = [
  'Address Not Matching',
  'Name Not Matching with Records',
  'Period of Stay Not Matching',
  'Different Person Living at Address',
  'Residence Status Mismatch',
  'Documents Found to be Fake',
  'Neighbour Denied Candidate Living There',
  'Other'
];

const VERIFICATION_MODES = [
  'Physical Visit',
  'Phone Verification',
  'Video Call',
  'Digital Verification',
  'Postal Verification',
  'Third Party Vendor',
  'Other'
];

const RESIDENCE_STATUS_OPTIONS = [
  'Own House',
  'Rented House',
  'Paying Guest',
  'Company Accommodation',
  'Hostel',
  'Relative\'s House',
  'Other'
];

export default function AddressVerificationForm({ 
  caseId, 
  checkId, 
  checkData, 
  employeeData, 
  documents = [],
  onBack,
  onComplete 
}) {
  const [loading, setLoading] = useState(false);
  const [showSendToVendor, setShowSendToVendor] = useState(false);
  const [showRecordResponse, setShowRecordResponse] = useState(false);
  const [showFieldApp, setShowFieldApp] = useState(false);
  const [verificationData, setVerificationData] = useState(checkData?.address_verification || {});
  const [vendors, setVendors] = useState([]);
  
  // Annexure state
  const [annexures, setAnnexures] = useState([]);
  const [showUploadAnnexure, setShowUploadAnnexure] = useState(false);
  const [uploadingAnnexure, setUploadingAnnexure] = useState(false);
  const [annexureForm, setAnnexureForm] = useState({
    file_name: '',
    file_url: '',
    file_type: 'image',
    description: '',
    annexure_type: 'house_photo_1',
    gps_latitude: '',
    gps_longitude: '',
    captured_at: ''
  });
  
  // Vendor email form state
  const [vendorForm, setVendorForm] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_email: '',
    additional_instructions: ''
  });
  
  // Response form state
  const [responseForm, setResponseForm] = useState({
    verifier_name: '',
    verifier_designation: '',
    verifier_contact: '',
    verification_mode: 'Physical Visit',
    verification_mode_other: '',
    date_of_visit: '',
    time_of_visit: '',
    overall_remarks: '',
    verification_result: '',
    gps_coordinates: '',
    fields: VERIFICATION_FIELDS.map(f => ({
      field_name: f.label,
      candidate_value: '',
      status: 'verified',
      reason: '',
      verified_value: '',
      remarks: ''
    }))
  });

  // Fetch annexures and vendors on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [annexuresRes, vendorsRes] = await Promise.all([
          axios.get(`/cases/${caseId}/checks/${checkId}/annexures`),
          axios.get('/vendors').catch(() => ({ data: [] }))
        ]);
        setAnnexures(annexuresRes.data || []);
        setVendors(vendorsRes.data || []);
      } catch (e) {
        console.error('Failed to fetch data');
      }
    };
    if (caseId && checkId) {
      fetchData();
    }
  }, [caseId, checkId]);

  // Pre-populate forms with employee data
  useEffect(() => {
    if (employeeData) {
      const address = employeeData.present_address || employeeData.permanent_address || {};
      
      const updatedFields = VERIFICATION_FIELDS.map(f => {
        let candidateValue = '';
        switch(f.id) {
          case 'candidate_name': candidateValue = employeeData.name || ''; break;
          case 'father_name': candidateValue = employeeData.father_name || ''; break;
          case 'contact_number': candidateValue = employeeData.phone || ''; break;
          case 'alternate_number': candidateValue = employeeData.alternate_phone || ''; break;
          case 'address': candidateValue = address.address_line || address.full_address || ''; break;
          case 'landmark': candidateValue = address.landmark || ''; break;
          case 'city': candidateValue = address.city || ''; break;
          case 'state': candidateValue = address.state || ''; break;
          case 'pincode': candidateValue = address.pincode || address.postal_code || ''; break;
          case 'period_of_stay': candidateValue = address.period_of_stay || ''; break;
          case 'residence_status': candidateValue = address.residence_status || ''; break;
          default: candidateValue = '';
        }
        return {
          field_name: f.label,
          candidate_value: candidateValue,
          status: 'verified',
          reason: '',
          verified_value: '',
          remarks: ''
        };
      });
      setResponseForm(prev => ({ ...prev, fields: updatedFields }));
    }
    
    // Load existing verification data
    if (checkData?.address_verification) {
      setVerificationData(checkData.address_verification);
    }
  }, [employeeData, checkData]);

  const handleUploadAnnexure = async () => {
    if (!annexureForm.file_url) {
      toast.error('Please provide file URL');
      return;
    }
    
    setUploadingAnnexure(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/annexures`, {
        case_id: caseId,
        check_id: checkId,
        ...annexureForm
      });
      setAnnexures(prev => [...prev, res.data]);
      setShowUploadAnnexure(false);
      setAnnexureForm({
        file_name: '',
        file_url: '',
        file_type: 'image',
        description: '',
        annexure_type: 'house_photo_1',
        gps_latitude: '',
        gps_longitude: '',
        captured_at: ''
      });
      toast.success('Photo/Document uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload');
    } finally {
      setUploadingAnnexure(false);
    }
  };
  
  const handleDeleteAnnexure = async (annexureId) => {
    if (!confirm('Delete this photo/document?')) return;
    
    try {
      await axios.delete(`/annexures/${annexureId}`);
      setAnnexures(prev => prev.filter(a => a.id !== annexureId));
      toast.success('Deleted successfully');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSendToVendor = async () => {
    if (!vendorForm.vendor_email) {
      toast.error('Please select a vendor or enter email');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`/cases/${caseId}/checks/${checkId}/send-address-verification`, {
        ...vendorForm,
        employee_name: employeeData?.name,
        father_name: employeeData?.father_name,
        address: employeeData?.present_address || employeeData?.permanent_address,
        contact_number: employeeData?.phone,
        alternate_number: employeeData?.alternate_phone
      });
      toast.success('Verification request sent to vendor (MOCKED)');
      setVerificationData(prev => ({
        ...prev,
        initiated_at: res.data.initiated_at,
        vendor_assigned: true,
        vendor_name: vendorForm.vendor_name,
        vendor_email: vendorForm.vendor_email
      }));
      setShowSendToVendor(false);
    } catch (error) {
      toast.error('Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const handleRecordResponse = async () => {
    if (!responseForm.verifier_name || !responseForm.date_of_visit) {
      toast.error('Please fill verifier name and date of visit');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.put(`/cases/${caseId}/checks/${checkId}/record-address-verification`, responseForm);
      toast.success(`Verification recorded - Result: ${res.data.overall_result.toUpperCase()}`);
      setVerificationData(prev => ({
        ...prev,
        ...res.data,
        fields: responseForm.fields,
        verifier_name: responseForm.verifier_name,
        verifier_designation: responseForm.verifier_designation
      }));
      setShowRecordResponse(false);
      if (onComplete) onComplete(res.data);
    } catch (error) {
      toast.error('Failed to record verification');
    } finally {
      setLoading(false);
    }
  };

  const updateFieldStatus = (index, field, value) => {
    setResponseForm(prev => {
      const newFields = [...prev.fields];
      newFields[index] = { ...newFields[index], [field]: value };
      return { ...prev, fields: newFields };
    });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'verified': return <CheckCircle className="w-5 h-5 text-emerald-600" />;
      case 'not_verified': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
      case 'discrepancy': return <XCircle className="w-5 h-5 text-rose-600" />;
      default: return null;
    }
  };

  const getResultBadge = (result) => {
    switch(result) {
      case 'green': return <Badge className="bg-emerald-100 text-emerald-700">GREEN - Verified</Badge>;
      case 'amber': return <Badge className="bg-amber-100 text-amber-700">AMBER - Unable to Verify</Badge>;
      case 'red': return <Badge className="bg-rose-100 text-rose-700">RED - Discrepancy</Badge>;
      default: return <Badge variant="outline">Pending</Badge>;
    }
  };

  const getPhotoRequirements = () => {
    const required = [
      { type: 'house_photo_1', label: 'House Photo 1', found: annexures.some(a => a.annexure_type === 'house_photo_1') },
      { type: 'house_photo_2', label: 'House Photo 2', found: annexures.some(a => a.annexure_type === 'house_photo_2') },
      { type: 'landmark_photo_1', label: 'Landmark Photo 1', found: annexures.some(a => a.annexure_type === 'landmark_photo_1') },
      { type: 'landmark_photo_2', label: 'Landmark Photo 2', found: annexures.some(a => a.annexure_type === 'landmark_photo_2') },
      { type: 'verifier_id_proof', label: 'Verifier ID Proof', found: annexures.some(a => a.annexure_type === 'verifier_id_proof') },
      { type: 'fe_selfie', label: 'FE Selfie', found: annexures.some(a => a.annexure_type === 'fe_selfie') }
    ];
    return required;
  };

  const addressDocs = documents.filter(d => 
    d.check_type === 'address_check' || 
    ['address_proof', 'utility_bill', 'ration_card', 'passport'].includes(d.document_type)
  );

  const fieldAppLink = `${window.location.origin}/field-verification/${caseId}/${checkId}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Address Verification</h2>
            <p className="text-slate-600">Case ID: {caseId} | Check ID: {checkId}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getResultBadge(verificationData?.overall_result)}
        </div>
      </div>

      {/* TAT Info */}
      {verificationData?.initiated_at && (
        <Card className="border-teal-200 bg-teal-50">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Clock className="w-6 h-6 text-teal-600" />
                <div>
                  <p className="font-medium text-teal-900">Verification Timeline</p>
                  <p className="text-sm text-teal-700">
                    Initiated: {new Date(verificationData.initiated_at).toLocaleString()}
                    {verificationData.completed_at && (
                      <> | Completed: {new Date(verificationData.completed_at).toLocaleString()}</>
                    )}
                    {verificationData.tat_hours && (
                      <> | TAT: {verificationData.tat_hours} hours</>
                    )}
                  </p>
                </div>
              </div>
              {verificationData.vendor_assigned && (
                <Badge className="bg-teal-100 text-teal-700">
                  <Building2 className="w-3 h-3 mr-1" />
                  Assigned to: {verificationData.vendor_name}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Candidate Address Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-teal-600" />
            Candidate Address Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          {employeeData ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-500">Candidate Name</Label>
                <p className="font-medium">{employeeData.name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Father&apos;s Name</Label>
                <p className="font-medium">{employeeData.father_name || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Contact Number</Label>
                <p className="font-medium">{employeeData.phone || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Alternate Number</Label>
                <p className="font-medium">{employeeData.alternate_phone || 'N/A'}</p>
              </div>
              <div className="col-span-2">
                <Label className="text-slate-500">Full Address</Label>
                <p className="font-medium">
                  {employeeData.present_address?.address_line || employeeData.present_address?.full_address || 'N/A'}
                </p>
              </div>
              <div>
                <Label className="text-slate-500">Landmark</Label>
                <p className="font-medium">{employeeData.present_address?.landmark || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">City</Label>
                <p className="font-medium">{employeeData.present_address?.city || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">State</Label>
                <p className="font-medium">{employeeData.present_address?.state || 'N/A'}</p>
              </div>
              <div>
                <Label className="text-slate-500">Pincode</Label>
                <p className="font-medium">{employeeData.present_address?.pincode || 'N/A'}</p>
              </div>
            </div>
          ) : (
            <p className="text-slate-500">No address information available</p>
          )}
        </CardContent>
      </Card>

      {/* Client/Entity Info */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Building2 className="w-5 h-5" />
            Client Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <Label className="text-blue-600">Client Name</Label>
              <p className="font-medium text-blue-900">{checkData?.client_name || employeeData?.client_name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-blue-600">Entity (Joining Company)</Label>
              <p className="font-medium text-blue-900">{checkData?.entity_name || employeeData?.entity_name || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-blue-600">Verification Type</Label>
              <p className="font-medium text-blue-900">{checkData?.verification_type || 'Address Verification'}</p>
            </div>
            <div>
              <Label className="text-blue-600">Initiated Date</Label>
              <p className="font-medium text-blue-900">
                {verificationData?.initiated_at 
                  ? new Date(verificationData.initiated_at).toLocaleDateString() 
                  : new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Photo Requirements Status */}
      <Card className="border-amber-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-amber-600" />
            Photo Requirements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {getPhotoRequirements().map((req, idx) => (
              <div 
                key={idx} 
                className={`p-3 rounded-lg border text-center ${
                  req.found ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'
                }`}
              >
                {req.found ? (
                  <CheckCircle className="w-6 h-6 text-emerald-600 mx-auto mb-1" />
                ) : (
                  <Camera className="w-6 h-6 text-slate-400 mx-auto mb-1" />
                )}
                <p className={`text-xs font-medium ${req.found ? 'text-emerald-700' : 'text-slate-500'}`}>
                  {req.label}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Candidate Documents */}
      {addressDocs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Candidate Documents ({addressDocs.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {addressDocs.map((doc, idx) => (
                <Button 
                  key={idx} 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(doc.file_url, '_blank')}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {doc.document_type}
                  <Download className="w-3 h-3 ml-2" />
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Annexures / Verification Photos */}
      <Card className="border-teal-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5 text-teal-600" />
            Verification Photos & Documents ({annexures.length}/6 minimum)
          </CardTitle>
          <Button onClick={() => setShowUploadAnnexure(true)} size="sm" className="bg-teal-600 hover:bg-teal-700" data-testid="addr-upload-photo-btn">
            <Upload className="w-4 h-4 mr-2" />
            Upload Photo
          </Button>
        </CardHeader>
        <CardContent>
          {annexures.length === 0 ? (
            <div className="text-center py-8">
              <Camera className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                No photos uploaded yet. Required: 2 house photos, 2 landmark photos, 1 verifier ID, 1 FE selfie
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {annexures.map((annexure, idx) => (
                <div key={idx} className="border rounded-lg p-3 bg-slate-50 relative group" data-testid={`addr-annexure-item-${idx}`}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-rose-600 h-6 w-6 p-0"
                    onClick={() => handleDeleteAnnexure(annexure.id)}
                    data-testid={`addr-delete-annexure-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  <div className="flex items-center gap-2 mb-2">
                    <Image className="w-5 h-5 text-teal-600" />
                    <Badge variant="outline" className="text-xs">
                      {ANNEXURE_TYPES.find(t => t.id === annexure.annexure_type)?.label || annexure.annexure_type}
                    </Badge>
                  </div>
                  <p className="text-sm font-medium truncate">{annexure.file_name || 'Photo'}</p>
                  {annexure.gps_latitude && annexure.gps_longitude && (
                    <p className="text-xs text-teal-600 flex items-center gap-1 mt-1">
                      <Navigation className="w-3 h-3" />
                      GPS: {annexure.gps_latitude}, {annexure.gps_longitude}
                    </p>
                  )}
                  {annexure.description && (
                    <p className="text-xs text-slate-500 truncate">{annexure.description}</p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full text-xs h-7"
                      onClick={() => window.open(annexure.file_url, '_blank')}
                      data-testid={`addr-view-annexure-${idx}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">
                    {annexure.uploaded_at ? new Date(annexure.uploaded_at).toLocaleDateString() : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Verification App Link */}
      <Card className="border-purple-200 bg-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-purple-900">
            <Phone className="w-5 h-5" />
            Field Verification App
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex-1">
              <p className="text-sm text-purple-700 mb-2">
                Share this link with the Field Executive to capture photos with GPS coordinates directly from their mobile device.
              </p>
              <div className="flex items-center gap-2">
                <Input 
                  value={fieldAppLink} 
                  readOnly 
                  className="bg-white text-sm"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(fieldAppLink);
                    toast.success('Link copied to clipboard');
                  }}
                  className="border-purple-300"
                >
                  Copy
                </Button>
              </div>
            </div>
            <Button 
              onClick={() => window.open(fieldAppLink, '_blank')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Field App
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Field Verification Submitted Data */}
      {(verificationData?.field_verification_submitted || checkData?.field_verification_submitted) && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-emerald-900">
                <CheckCircle className="w-5 h-5" />
                Field Verification Submitted
              </CardTitle>
              <Badge className="bg-emerald-100 text-emerald-700">
                {verificationData?.field_submitted_at ? new Date(verificationData.field_submitted_at).toLocaleDateString() : 'Submitted'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Verifier Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Field Verifier</p>
                <p className="font-medium">{verificationData?.field_verifier_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Contact</p>
                <p className="font-medium">{verificationData?.field_verifier_contact || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Date of Visit</p>
                <p className="font-medium">{verificationData?.field_date_of_visit || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Time of Visit</p>
                <p className="font-medium">{verificationData?.field_time_of_visit || '-'}</p>
              </div>
            </div>

            {/* GPS Coordinates */}
            {verificationData?.field_gps_coordinates && (
              <div>
                <p className="text-xs text-emerald-600 font-medium">GPS Coordinates</p>
                <p className="font-medium text-sm flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-emerald-600" />
                  {verificationData.field_gps_coordinates}
                  <a 
                    href={`https://maps.google.com/?q=${verificationData.field_gps_coordinates}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 underline text-xs"
                  >
                    View on Map
                  </a>
                </p>
              </div>
            )}

            {/* Verification Details */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 border-t pt-4">
              <div>
                <p className="text-xs text-emerald-600 font-medium">Residence Confirmed</p>
                <Badge className={verificationData?.field_residence_confirmed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                  {verificationData?.field_residence_confirmed || '-'}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Residence Status</p>
                <p className="font-medium">{verificationData?.field_residence_status || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Period of Stay</p>
                <p className="font-medium">{verificationData?.field_period_of_stay || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Person Met</p>
                <p className="font-medium">{verificationData?.field_person_met_name || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Relation</p>
                <p className="font-medium">{verificationData?.field_person_met_relation || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Person Contact</p>
                <p className="font-medium">{verificationData?.field_person_met_contact || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-emerald-600 font-medium">Neighbour Confirmed</p>
                <Badge className={verificationData?.field_neighbour_confirmed === 'yes' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                  {verificationData?.field_neighbour_confirmed || '-'}
                </Badge>
              </div>
            </div>

            {/* Field Remarks */}
            {verificationData?.field_overall_remarks && (
              <div className="border-t pt-4">
                <p className="text-xs text-emerald-600 font-medium mb-1">Field Agent Remarks</p>
                <p className="text-sm bg-white p-3 rounded border border-emerald-200">{verificationData.field_overall_remarks}</p>
              </div>
            )}

            {/* Field Photos */}
            {verificationData?.field_photos && verificationData.field_photos.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-xs text-emerald-600 font-medium mb-2">Field Photos ({verificationData.field_photos.length})</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {verificationData.field_photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <a href={photo.url || photo} target="_blank" rel="noopener noreferrer">
                        <img 
                          src={photo.url || photo} 
                          alt={photo.label || photo.type || `Photo ${idx + 1}`}
                          className="w-full h-24 object-cover rounded border border-emerald-200 hover:opacity-80 transition-opacity"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 text-center rounded-b">
                          {photo.label || photo.type || `Photo ${idx + 1}`}
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-4">
        <Button onClick={() => setShowSendToVendor(true)} className="bg-blue-600 hover:bg-blue-700">
          <Mail className="w-4 h-4 mr-2" />
          Send to Vendor
        </Button>
        <Button onClick={() => setShowRecordResponse(true)} className="bg-emerald-600 hover:bg-emerald-700">
          <CheckCircle className="w-4 h-4 mr-2" />
          Record Verification Response
        </Button>
      </div>

      {/* Verification Results (if completed) */}
      {verificationData?.fields && verificationData.fields.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Verification Results
              </span>
              {getResultBadge(verificationData.overall_result)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field</TableHead>
                  <TableHead>Candidate Value</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason/Remarks</TableHead>
                  <TableHead>Verified Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {verificationData.fields.map((field, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{field.field_name}</TableCell>
                    <TableCell>{field.candidate_value || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(field.status)}
                        <span className="capitalize">{field.status?.replace('_', ' ')}</span>
                      </div>
                    </TableCell>
                    <TableCell>{field.reason || field.remarks || '-'}</TableCell>
                    <TableCell>{field.verified_value || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Verifier Info */}
            {verificationData.verifier_name && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2">Verified By</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <Label className="text-slate-500">Verifier Name</Label>
                    <p>{verificationData.verifier_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Designation</Label>
                    <p>{verificationData.verifier_designation}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Date of Visit</Label>
                    <p>{verificationData.date_of_visit}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Verification Mode</Label>
                    <p>{verificationData.verification_mode}</p>
                  </div>
                </div>
                {verificationData.gps_coordinates && (
                  <div className="mt-2">
                    <Label className="text-slate-500">GPS Coordinates</Label>
                    <p className="font-medium flex items-center gap-1">
                      <Navigation className="w-4 h-4 text-teal-600" />
                      {verificationData.gps_coordinates}
                    </p>
                  </div>
                )}
                {verificationData.overall_remarks && (
                  <div className="mt-2">
                    <Label className="text-slate-500">Overall Remarks</Label>
                    <p>{verificationData.overall_remarks}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Send to Vendor Dialog */}
      <Dialog open={showSendToVendor} onOpenChange={setShowSendToVendor}>
        <DialogContent className="max-w-lg" data-testid="addr-send-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Send Address Verification to Vendor
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm">
              <p className="font-medium text-blue-900">Email Subject:</p>
              <p className="text-blue-700">
                New Case Assigned - {new Date().toLocaleDateString()} - {vendorForm.vendor_name || '[Vendor Name]'}
              </p>
            </div>
            
            {vendors.length > 0 && (
              <div>
                <Label>Select Vendor</Label>
                <Select 
                  value={vendorForm.vendor_id}
                  onValueChange={(v) => {
                    const vendor = vendors.find(vd => vd.id === v);
                    setVendorForm(prev => ({
                      ...prev,
                      vendor_id: v,
                      vendor_name: vendor?.name || '',
                      vendor_email: vendor?.email || ''
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div>
              <Label>Vendor Name *</Label>
              <Input 
                value={vendorForm.vendor_name}
                onChange={(e) => setVendorForm(prev => ({ ...prev, vendor_name: e.target.value }))}
                placeholder="Enter vendor name"
              />
            </div>
            <div>
              <Label>Vendor Email *</Label>
              <Input 
                type="email"
                value={vendorForm.vendor_email}
                onChange={(e) => setVendorForm(prev => ({ ...prev, vendor_email: e.target.value }))}
                placeholder="vendor@company.com"
              />
            </div>
            <div>
              <Label>Additional Instructions</Label>
              <Textarea 
                value={vendorForm.additional_instructions}
                onChange={(e) => setVendorForm(prev => ({ ...prev, additional_instructions: e.target.value }))}
                placeholder="Any special instructions for the vendor..."
                rows={3}
              />
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg border text-sm">
              <p className="font-medium mb-2">Email will include:</p>
              <ul className="list-disc list-inside space-y-1 text-slate-600">
                <li>Check ID: {checkId}</li>
                <li>Initiated Date: {new Date().toLocaleDateString()}</li>
                <li>Client/Entity Name</li>
                <li>Employee Name, Father Name</li>
                <li>Full Address with Landmark, City, State, Pincode</li>
                <li>Contact Numbers</li>
                <li>Field Verification App Link</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendToVendor(false)}>Cancel</Button>
            <Button onClick={handleSendToVendor} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              {loading ? 'Sending...' : 'Send to Vendor'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Record Response Dialog */}
      <Dialog open={showRecordResponse} onOpenChange={setShowRecordResponse}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="addr-record-response-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Record Address Verification Response
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Verifier Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-slate-50 rounded-lg">
              <div>
                <Label>Verifier/Executive Name *</Label>
                <Input 
                  value={responseForm.verifier_name}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_name: e.target.value }))}
                  placeholder="Field Executive Name"
                />
              </div>
              <div>
                <Label>Designation</Label>
                <Input 
                  value={responseForm.verifier_designation}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_designation: e.target.value }))}
                  placeholder="Field Verifier"
                />
              </div>
              <div>
                <Label>Contact Number</Label>
                <Input 
                  value={responseForm.verifier_contact}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, verifier_contact: e.target.value }))}
                  placeholder="+91-XXXXXXXXXX"
                />
              </div>
              <div>
                <Label>GPS Coordinates</Label>
                <Input 
                  value={responseForm.gps_coordinates}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, gps_coordinates: e.target.value }))}
                  placeholder="12.9716, 77.5946"
                />
              </div>
            </div>

            {/* Verification Mode & Date */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-teal-50 rounded-lg border border-teal-200">
              <div>
                <Label>Verification Mode *</Label>
                <Select 
                  value={responseForm.verification_mode}
                  onValueChange={(v) => setResponseForm(prev => ({ ...prev, verification_mode: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {VERIFICATION_MODES.map((mode, idx) => (
                      <SelectItem key={idx} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {responseForm.verification_mode === 'Other' && (
                <div>
                  <Label>Specify Mode</Label>
                  <Input 
                    value={responseForm.verification_mode_other}
                    onChange={(e) => setResponseForm(prev => ({ ...prev, verification_mode_other: e.target.value }))}
                    placeholder="Enter mode"
                  />
                </div>
              )}
              <div>
                <Label>Date of Visit *</Label>
                <Input 
                  type="date"
                  value={responseForm.date_of_visit}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, date_of_visit: e.target.value }))}
                />
              </div>
              <div>
                <Label>Time of Visit</Label>
                <Input 
                  type="time"
                  value={responseForm.time_of_visit}
                  onChange={(e) => setResponseForm(prev => ({ ...prev, time_of_visit: e.target.value }))}
                />
              </div>
            </div>

            {/* Field Verification */}
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-100">
                    <TableHead className="w-[200px]">Field</TableHead>
                    <TableHead className="w-[150px]">Candidate Value</TableHead>
                    <TableHead className="w-[180px]">Verification Status</TableHead>
                    <TableHead>Reason (if not verified/discrepancy)</TableHead>
                    <TableHead className="w-[150px]">Verified Value</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {responseForm.fields.map((field, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">
                        {field.field_name}
                        {VERIFICATION_FIELDS[idx]?.required && <span className="text-rose-500 ml-1">*</span>}
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={field.candidate_value}
                          onChange={(e) => updateFieldStatus(idx, 'candidate_value', e.target.value)}
                          className="h-8 text-sm"
                          placeholder="-"
                        />
                      </TableCell>
                      <TableCell>
                        <RadioGroup 
                          value={field.status}
                          onValueChange={(v) => updateFieldStatus(idx, 'status', v)}
                          className="flex flex-col gap-1"
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="verified" id={`addr-verified-${idx}`} />
                            <Label htmlFor={`addr-verified-${idx}`} className="text-xs text-emerald-600 cursor-pointer">
                              ✓ Verified
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="not_verified" id={`addr-not-verified-${idx}`} />
                            <Label htmlFor={`addr-not-verified-${idx}`} className="text-xs text-amber-600 cursor-pointer">
                              ! Not Verified
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="discrepancy" id={`addr-discrepancy-${idx}`} />
                            <Label htmlFor={`addr-discrepancy-${idx}`} className="text-xs text-rose-600 cursor-pointer">
                              ✗ Discrepancy
                            </Label>
                          </div>
                        </RadioGroup>
                      </TableCell>
                      <TableCell>
                        {field.status === 'not_verified' && (
                          <Select 
                            value={field.reason}
                            onValueChange={(v) => updateFieldStatus(idx, 'reason', v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select reason" />
                            </SelectTrigger>
                            <SelectContent>
                              {NOT_VERIFIED_REASONS.map((r, i) => (
                                <SelectItem key={i} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {field.status === 'discrepancy' && (
                          <Select 
                            value={field.reason}
                            onValueChange={(v) => updateFieldStatus(idx, 'reason', v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select reason" />
                            </SelectTrigger>
                            <SelectContent>
                              {DISCREPANCY_REASONS.map((r, i) => (
                                <SelectItem key={i} value={r}>{r}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {(field.status === 'not_verified' || field.status === 'discrepancy') && field.reason === 'Other' && (
                          <Input 
                            className="mt-2 h-8 text-xs"
                            placeholder="Specify reason"
                            value={field.remarks}
                            onChange={(e) => updateFieldStatus(idx, 'remarks', e.target.value)}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {field.status === 'discrepancy' && (
                          <Input 
                            value={field.verified_value}
                            onChange={(e) => updateFieldStatus(idx, 'verified_value', e.target.value)}
                            className="h-8 text-sm"
                            placeholder="Correct value"
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Overall Remarks */}
            <div>
              <Label>Overall Remarks / Executive Comments</Label>
              <Textarea 
                value={responseForm.overall_remarks}
                onChange={(e) => setResponseForm(prev => ({ ...prev, overall_remarks: e.target.value }))}
                placeholder="Any additional observations about the address verification..."
                rows={3}
              />
            </div>

            {/* Legend */}
            <div className="flex gap-6 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <span>All Verified = GREEN</span>
              </div>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span>Any Not Verified = AMBER</span>
              </div>
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-rose-600" />
                <span>Any Discrepancy = RED</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRecordResponse(false)}>Cancel</Button>
            <Button onClick={handleRecordResponse} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700">
              <CheckCircle className="w-4 h-4 mr-2" />
              {loading ? 'Saving...' : 'Save Verification'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Photo Dialog */}
      <Dialog open={showUploadAnnexure} onOpenChange={setShowUploadAnnexure}>
        <DialogContent className="max-w-lg" data-testid="addr-upload-photo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-teal-600" />
              Upload Verification Photo/Document
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Photo/Document Type *</Label>
              <Select 
                value={annexureForm.annexure_type}
                onValueChange={(v) => setAnnexureForm(prev => ({ ...prev, annexure_type: v }))}
              >
                <SelectTrigger data-testid="addr-photo-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ANNEXURE_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File Name</Label>
              <Input 
                value={annexureForm.file_name}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_name: e.target.value }))}
                placeholder="e.g., House_Front_View"
                data-testid="addr-photo-filename-input"
              />
            </div>
            <div>
              <Label>File URL *</Label>
              <Input 
                value={annexureForm.file_url}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, file_url: e.target.value }))}
                placeholder="https://example.com/photo.jpg"
                data-testid="addr-photo-url-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Upload to cloud storage or use Field App for GPS-tagged photos
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>GPS Latitude</Label>
                <Input 
                  value={annexureForm.gps_latitude}
                  onChange={(e) => setAnnexureForm(prev => ({ ...prev, gps_latitude: e.target.value }))}
                  placeholder="12.9716"
                  data-testid="addr-photo-lat-input"
                />
              </div>
              <div>
                <Label>GPS Longitude</Label>
                <Input 
                  value={annexureForm.gps_longitude}
                  onChange={(e) => setAnnexureForm(prev => ({ ...prev, gps_longitude: e.target.value }))}
                  placeholder="77.5946"
                  data-testid="addr-photo-lng-input"
                />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={annexureForm.description}
                onChange={(e) => setAnnexureForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of the photo..."
                rows={2}
                data-testid="addr-photo-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadAnnexure(false)}>Cancel</Button>
            <Button 
              onClick={handleUploadAnnexure} 
              disabled={uploadingAnnexure || !annexureForm.file_url} 
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="addr-photo-submit-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadingAnnexure ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
